import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fmtMoney } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Bot, Tag, ShieldCheck, Clock, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function DealDetail() {
  const { id } = useParams();
  const [offer, setOffer] = useState(null);
  const [explanation, setExplanation] = useState("");
  const [loadingExp, setLoadingExp] = useState(false);

  useEffect(() => {
    api.get(`/catalog/offers/${id}`).then((r) => setOffer(r.data)).catch(() => toast.error("Offer not found"));
  }, [id]);

  const explain = async () => {
    setLoadingExp(true);
    try {
      const { data } = await api.post("/ai/explain-deal", { offer_id: id });
      setExplanation(data.explanation);
    } catch (e) {
      toast.error("Couldn't generate explanation");
    } finally {
      setLoadingExp(false);
    }
  };

  const shop = async () => {
    try {
      await api.post("/wallet/click", { offer_id: id });
      await api.post("/wallet/simulate-purchase", { offer_id: id, order_amount: 100 });
      toast.success(`Cashback approved: ${fmtMoney((100 * (offer?.cashback_rate || 5)) / 200)}`);
    } catch (e) {
      toast.error("Couldn't shop");
    }
  };

  if (!offer) return <div className="text-gray-500">Loading deal…</div>;

  return (
    <div data-testid="page-deal-detail">
      <Link to="/app/feed" className="text-sm text-emerald-700 hover:underline mb-3 inline-block">← Back to feed</Link>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 border-gray-200 shadow-none rounded-2xl">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-4">
              {offer.merchant?.logo && <img src={offer.merchant.logo} alt={offer.merchant.name} className="w-12 h-12 rounded-xl object-contain bg-gray-50 p-1 border border-gray-100" />}
              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-gray-500 font-semibold">{offer.merchant_name}</div>
                <h1 className="font-display font-bold text-2xl tracking-tight">{offer.title}</h1>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-4">
              <Badge className="bg-emerald-50 text-emerald-800 border border-emerald-200 hover:bg-emerald-50 font-semibold">
                {offer.cashback_rate}% cashback
              </Badge>
              {offer.coupon_code && (
                <Badge className="bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-50">
                  <Tag className="w-3 h-3 mr-1" /> {offer.coupon_code}
                </Badge>
              )}
              {offer.merchant?.trust_score && (
                <Badge className="bg-sky-50 text-sky-800 border border-sky-200 hover:bg-sky-50">
                  <ShieldCheck className="w-3 h-3 mr-1" /> Trust {offer.merchant.trust_score}
                </Badge>
              )}
              {offer.expiration && (
                <Badge variant="outline">
                  <Clock className="w-3 h-3 mr-1" /> Expires {new Date(offer.expiration).toLocaleDateString()}
                </Badge>
              )}
            </div>

            <p className="text-gray-700 leading-relaxed mb-6">{offer.description}</p>

            <Button onClick={shop} data-testid="dd-shop" className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full px-8">
              Shop now & activate cashback <ArrowRight className="w-4 h-4 ml-2" />
            </Button>

            <div className="mt-8 pt-8 border-t border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-display font-bold text-base flex items-center gap-2">
                  <Bot className="w-4 h-4 text-sky-700" /> Why AI picked this for you
                </h3>
                <Button variant="outline" size="sm" onClick={explain} disabled={loadingExp} data-testid="dd-explain">
                  {loadingExp ? "Generating…" : explanation ? "Regenerate" : "Explain this deal"}
                </Button>
              </div>
              {explanation ? (
                <div className="bg-sky-50 border border-sky-100 rounded-2xl p-5 text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">
                  {explanation}
                </div>
              ) : (
                <p className="text-sm text-gray-500">Tap "Explain" to get AI analysis on why this deal is good, hidden fees, and better alternatives.</p>
              )}
            </div>

            <div className="mt-8 text-xs text-gray-500">
              <strong>Terms:</strong> {offer.terms || "Cashback is subject to merchant confirmation, tracking accuracy, return windows, and affiliate network approval."}
            </div>
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card className="border-gray-200 shadow-none rounded-2xl">
            <CardContent className="p-5">
              <div className="text-xs uppercase tracking-[0.18em] text-gray-500 font-semibold mb-2">Cashback timeline</div>
              <ul className="space-y-2 text-sm text-gray-700">
                <li>· Click tracked instantly</li>
                <li>· Purchase pending: 1–7 days</li>
                <li>· Merchant confirmed: ~30 days</li>
                <li>· Approved & withdrawable</li>
              </ul>
            </CardContent>
          </Card>

          <Card className="border-emerald-100 shadow-none rounded-2xl gradient-emerald">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-emerald-700" />
                <div className="text-xs uppercase tracking-[0.18em] text-emerald-700 font-semibold">Estimated savings</div>
              </div>
              <div className="font-display font-bold text-3xl">{fmtMoney((100 * offer.cashback_rate) / 200)}</div>
              <p className="text-xs text-gray-600 mt-1">on a $100 order (50% commission split)</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
